import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getAudioImageUrl,
  getNowPlayingTrackOrNull,
  resolveMediaUrl,
  type NowPlayingTrack,
  type PublicChannel
} from '../../api'

const NOW_PLAYING_REFRESH_MS = 10000
const LIVE_STREAM_LONG_LOADING_MS = 7000
const LIVE_STREAM_TIMEOUT_MS = 22000
const LIVE_STREAM_RETRY_BASE_MS = 1200
const LIVE_STREAM_RETRY_MAX_MS = 12000
const LIVE_STREAM_MAX_RECOVERY_ATTEMPTS = 8
const LIVE_STREAM_NOTICE_THROTTLE_MS = 6000
const LIVE_STREAM_BUFFER_RECOVERY_MS = 20000
const LIVE_STREAM_PROGRESS_STALE_MS = 25000
const LIVE_STREAM_PROGRESS_WATCH_MS = 5000

type PublicLiveStreamPlayerOptions = {
  onUserNotice?: (message: string) => void
}

const clampVolume = (value: number): number => Math.max(0, Math.min(1, value))

const supportsMediaSession = (): boolean =>
  typeof navigator !== 'undefined' && 'mediaSession' in navigator

const isBrowserOffline = (): boolean =>
  typeof navigator !== 'undefined' && navigator.onLine === false

export const usePublicLiveStreamPlayer = (options: PublicLiveStreamPlayerOptions = {}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const channelRef = useRef<PublicChannel | null>(null)
  const onUserNoticeRef = useRef(options.onUserNotice)
  const volumeRef = useRef(0.8)
  const requestGenerationRef = useRef(0)
  const longLoadingTimeoutRef = useRef<number | null>(null)
  const streamTimeoutRef = useRef<number | null>(null)
  const recoveryTimeoutRef = useRef<number | null>(null)
  const bufferRecoveryTimeoutRef = useRef<number | null>(null)
  const progressWatchIntervalRef = useRef<number | null>(null)
  const lastMediaProgressAtRef = useRef(Date.now())
  const reconnectAttemptsRef = useRef(0)
  const wantsPlaybackRef = useRef(false)
  const lastNoticeRef = useRef<{ message: string; shownAt: number } | null>(null)
  const reconnectRef = useRef<((reason: string) => void) | null>(null)
  const scheduleRecoveryAttemptRef = useRef<((reason: string) => void) | null>(null)

  const [channel, setChannel] = useState<PublicChannel | null>(null)
  const [currentTrack, setCurrentTrack] = useState<NowPlayingTrack | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isTakingLong, setIsTakingLong] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [volume, setVolumeState] = useState(0.8)

  useEffect(() => {
    onUserNoticeRef.current = options.onUserNotice
  }, [options.onUserNotice])

  const clearRecoveryTimer = useCallback((): void => {
    if (recoveryTimeoutRef.current !== null) {
      window.clearTimeout(recoveryTimeoutRef.current)
      recoveryTimeoutRef.current = null
    }
  }, [])

  const clearBufferRecoveryTimer = useCallback((): void => {
    if (bufferRecoveryTimeoutRef.current !== null) {
      window.clearTimeout(bufferRecoveryTimeoutRef.current)
      bufferRecoveryTimeoutRef.current = null
    }
  }, [])

  const markMediaProgress = useCallback((): void => {
    lastMediaProgressAtRef.current = Date.now()
  }, [])

  const clearProgressWatch = useCallback((): void => {
    if (progressWatchIntervalRef.current !== null) {
      window.clearInterval(progressWatchIntervalRef.current)
      progressWatchIntervalRef.current = null
    }
  }, [])

  const startProgressWatch = useCallback((): void => {
    if (progressWatchIntervalRef.current !== null) {
      return
    }

    progressWatchIntervalRef.current = window.setInterval(() => {
      const audio = audioRef.current

      if (!audio || !channelRef.current || !wantsPlaybackRef.current || recoveryTimeoutRef.current !== null) {
        return
      }

      if (audio.paused || audio.ended) {
        return
      }

      if (Date.now() - lastMediaProgressAtRef.current < LIVE_STREAM_PROGRESS_STALE_MS) {
        return
      }

      console.warn('Live stream stopped receiving media progress', {
        channel: channelRef.current.slug,
        networkState: audio.networkState,
        readyState: audio.readyState
      })
      setIsLoading(true)
      scheduleRecoveryAttemptRef.current?.('media-progress-stale')
    }, LIVE_STREAM_PROGRESS_WATCH_MS)
  }, [])

  const notifyUser = useCallback((message: string): void => {
    const now = Date.now()
    const lastNotice = lastNoticeRef.current

    if (lastNotice?.message === message && now - lastNotice.shownAt < LIVE_STREAM_NOTICE_THROTTLE_MS) {
      return
    }

    lastNoticeRef.current = { message, shownAt: now }
    onUserNoticeRef.current?.(message)
  }, [])

  const scheduleRecoveryAttempt = useCallback((reason: string): void => {
    if (!channelRef.current || !wantsPlaybackRef.current || recoveryTimeoutRef.current !== null) {
      return
    }

    const attempts = reconnectAttemptsRef.current
    const delay = isBrowserOffline()
      ? LIVE_STREAM_RETRY_MAX_MS
      : Math.min(LIVE_STREAM_RETRY_MAX_MS, LIVE_STREAM_RETRY_BASE_MS * 2 ** attempts)

    setErrorMessage(null)
    setIsLoading(!isBrowserOffline())
    notifyUser(isBrowserOffline() ? 'Connection lost.' : 'Connection lost. Reconnecting...')

    recoveryTimeoutRef.current = window.setTimeout(() => {
      recoveryTimeoutRef.current = null

      if (isBrowserOffline()) {
        scheduleRecoveryAttempt(reason)
        return
      }

      reconnectRef.current?.(reason)
    }, delay)
  }, [notifyUser])

  useEffect(() => {
    scheduleRecoveryAttemptRef.current = scheduleRecoveryAttempt

    return () => {
      scheduleRecoveryAttemptRef.current = null
    }
  }, [scheduleRecoveryAttempt])

  useEffect(() => {
    const audio = new Audio()

    audio.preload = 'none'
    audio.crossOrigin = 'anonymous'
    audio.volume = volumeRef.current
    audioRef.current = audio
    setAudioElement(audio)

    const clearLoadTimers = (): void => {
      if (longLoadingTimeoutRef.current !== null) {
        window.clearTimeout(longLoadingTimeoutRef.current)
        longLoadingTimeoutRef.current = null
      }

      if (streamTimeoutRef.current !== null) {
        window.clearTimeout(streamTimeoutRef.current)
        streamTimeoutRef.current = null
      }
    }

    const scheduleBufferRecovery = (reason: string): void => {
      if (!channelRef.current || !wantsPlaybackRef.current || bufferRecoveryTimeoutRef.current !== null) {
        return
      }

      bufferRecoveryTimeoutRef.current = window.setTimeout(() => {
        bufferRecoveryTimeoutRef.current = null

        if (!channelRef.current || !wantsPlaybackRef.current) {
          return
        }

        if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
          return
        }

        console.warn('Live stream buffering did not recover', {
          channel: channelRef.current.slug,
          networkState: audio.networkState,
          readyState: audio.readyState,
          reason
        })
        scheduleRecoveryAttempt(reason)
      }, LIVE_STREAM_BUFFER_RECOVERY_MS)
    }

    const handlePlay = (): void => {
      markMediaProgress()
      startProgressWatch()
      setIsPlaying(true)
    }
    const handlePause = (): void => {
      clearProgressWatch()
      setIsPlaying(false)
    }
    const handleError = (): void => {
      if (channelRef.current) {
        console.warn('Live stream playback error', {
          channel: channelRef.current.slug,
          error: audio.error
        })
        scheduleRecoveryAttempt('media-error')
      }
      clearLoadTimers()
      setIsTakingLong(false)
      setIsPlaying(false)
    }
    const handleLoadStart = (): void => {
      if (channelRef.current) {
        setIsLoading(true)
      }
    }
    const handleWaiting = (): void => {
      if (channelRef.current) {
        setIsLoading(true)
        scheduleBufferRecovery('stream-waiting')
      }
    }
    const handleCanPlay = (): void => {
      markMediaProgress()
      startProgressWatch()
      clearLoadTimers()
      clearRecoveryTimer()
      clearBufferRecoveryTimer()
      reconnectAttemptsRef.current = 0
      setIsTakingLong(false)
      setIsLoading(false)
      setErrorMessage(null)
    }
    const handleBufferProgress = (): void => {
      if (channelRef.current) {
        markMediaProgress()
        clearBufferRecoveryTimer()
      }
    }
    const handleOffline = (): void => {
      if (channelRef.current && wantsPlaybackRef.current) {
        console.warn('Live stream network went offline', { channel: channelRef.current.slug })
        notifyUser('Connection lost.')
        setIsLoading(false)
        scheduleRecoveryAttempt('network-offline')
      }
    }
    const handleOnline = (): void => {
      if (channelRef.current && wantsPlaybackRef.current) {
        clearRecoveryTimer()
        notifyUser('Connection restored. Reconnecting...')
        reconnectRef.current?.('network-online')
      }
    }

    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('error', handleError)
    audio.addEventListener('loadstart', handleLoadStart)
    audio.addEventListener('stalled', handleWaiting)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('playing', handleCanPlay)
    audio.addEventListener('timeupdate', handleBufferProgress)
    audio.addEventListener('progress', handleBufferProgress)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('loadstart', handleLoadStart)
      audio.removeEventListener('stalled', handleWaiting)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('playing', handleCanPlay)
      audio.removeEventListener('timeupdate', handleBufferProgress)
      audio.removeEventListener('progress', handleBufferProgress)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      clearLoadTimers()
      clearRecoveryTimer()
      clearBufferRecoveryTimer()
      clearProgressWatch()
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      audioRef.current = null
      setAudioElement(null)
    }
  }, [
    clearBufferRecoveryTimer,
    clearProgressWatch,
    clearRecoveryTimer,
    markMediaProgress,
    notifyUser,
    scheduleRecoveryAttempt,
    startProgressWatch
  ])

  const clearLoadTimers = useCallback((): void => {
    if (longLoadingTimeoutRef.current !== null) {
      window.clearTimeout(longLoadingTimeoutRef.current)
      longLoadingTimeoutRef.current = null
    }

    if (streamTimeoutRef.current !== null) {
      window.clearTimeout(streamTimeoutRef.current)
      streamTimeoutRef.current = null
    }
  }, [])

  const beginConnectionWatch = useCallback((generation: number): void => {
    clearLoadTimers()
    setIsTakingLong(false)

    longLoadingTimeoutRef.current = window.setTimeout(() => {
      if (generation === requestGenerationRef.current && channelRef.current) {
        setIsTakingLong(true)
      }
    }, LIVE_STREAM_LONG_LOADING_MS)

    streamTimeoutRef.current = window.setTimeout(() => {
      if (generation !== requestGenerationRef.current || !channelRef.current) {
        return
      }

      audioRef.current?.pause()
      setIsLoading(false)
      setIsPlaying(false)
      setIsTakingLong(false)
      console.warn('Live stream connection timed out', { channel: channelRef.current.slug })

      if (wantsPlaybackRef.current && reconnectAttemptsRef.current < LIVE_STREAM_MAX_RECOVERY_ATTEMPTS) {
        scheduleRecoveryAttempt('connection-timeout')
      } else {
        setErrorMessage('Live stream did not start. You can retry or choose another channel.')
      }
    }, LIVE_STREAM_TIMEOUT_MS)
  }, [clearLoadTimers, scheduleRecoveryAttempt])

  const refreshNowPlaying = useCallback(async (
    targetChannel = channelRef.current,
    generation = requestGenerationRef.current
  ): Promise<void> => {
    if (!targetChannel) {
      setCurrentTrack(null)
      return
    }

    if (isBrowserOffline()) {
      return
    }

    try {
      const track = await getNowPlayingTrackOrNull(targetChannel.slug)

      if (generation === requestGenerationRef.current) {
        setCurrentTrack(track)
      }
    } catch {
      if (generation === requestGenerationRef.current) {
        setCurrentTrack(null)
      }
    }
  }, [])

  const stop = useCallback((): void => {
    requestGenerationRef.current += 1
    const audio = audioRef.current

    wantsPlaybackRef.current = false
    reconnectAttemptsRef.current = 0
    clearLoadTimers()
    clearRecoveryTimer()
    clearBufferRecoveryTimer()
    clearProgressWatch()
    audio?.pause()
    audio?.removeAttribute('src')
    audio?.load()
    channelRef.current = null
    setChannel(null)
    setCurrentTrack(null)
    setErrorMessage(null)
    setIsLoading(false)
    setIsTakingLong(false)
    setIsPlaying(false)
  }, [clearBufferRecoveryTimer, clearLoadTimers, clearProgressWatch, clearRecoveryTimer])

  const reconnectCurrentStream = useCallback(async (reason: string): Promise<void> => {
    const currentChannel = channelRef.current
    const audio = audioRef.current

    if (!currentChannel || !audio || !wantsPlaybackRef.current) {
      return
    }

    if (isBrowserOffline()) {
      scheduleRecoveryAttempt(reason)
      return
    }

    if (reconnectAttemptsRef.current >= LIVE_STREAM_MAX_RECOVERY_ATTEMPTS) {
      clearLoadTimers()
      setIsLoading(false)
      setIsPlaying(false)
      setIsTakingLong(false)
      setErrorMessage('Connection lost. Use retry when the network is stable.')
      return
    }

    reconnectAttemptsRef.current += 1
    requestGenerationRef.current += 1
    const generation = requestGenerationRef.current

    console.info('Reconnecting live stream', {
      attempt: reconnectAttemptsRef.current,
      channel: currentChannel.slug,
      reason
    })

    setErrorMessage(null)
    setIsLoading(true)
    setIsPlaying(false)
    setIsTakingLong(false)
    markMediaProgress()
    startProgressWatch()
    audio.pause()
    audio.src = resolveMediaUrl(currentChannel.stream_url)
    audio.volume = volumeRef.current
    audio.load()
    beginConnectionWatch(generation)

    try {
      await audio.play()
      setErrorMessage(null)
      void refreshNowPlaying(currentChannel, generation)
    } catch (error) {
      if (generation !== requestGenerationRef.current || !wantsPlaybackRef.current) {
        return
      }

      console.warn('Live stream reconnect failed', {
        attempt: reconnectAttemptsRef.current,
        channel: currentChannel.slug,
        error,
        reason
      })
      clearLoadTimers()
      clearProgressWatch()
      setIsPlaying(false)
      setIsTakingLong(false)
      scheduleRecoveryAttempt('reconnect-failed')
    }
  }, [
    beginConnectionWatch,
    clearLoadTimers,
    clearProgressWatch,
    markMediaProgress,
    refreshNowPlaying,
    scheduleRecoveryAttempt,
    startProgressWatch
  ])

  useEffect(() => {
    reconnectRef.current = (reason: string) => {
      void reconnectCurrentStream(reason)
    }

    return () => {
      reconnectRef.current = null
    }
  }, [reconnectCurrentStream])

  const playChannel = useCallback(async (nextChannel: PublicChannel): Promise<void> => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    const isSameChannel = channelRef.current?.slug === nextChannel.slug

    if (isSameChannel) {
      if (audio.paused) {
        requestGenerationRef.current += 1
        const generation = requestGenerationRef.current

        try {
          wantsPlaybackRef.current = true
          markMediaProgress()
          startProgressWatch()
          setIsLoading(true)
          setIsTakingLong(false)
          beginConnectionWatch(generation)
          await audio.play()
          setErrorMessage(null)
        } catch (error) {
          console.warn('Unable to start live stream playback', { channel: nextChannel.slug, error })
          clearLoadTimers()
          clearProgressWatch()
          wantsPlaybackRef.current = false
          setErrorMessage('Live stream could not start. Use retry or choose another channel.')
          setIsPlaying(false)
          setIsLoading(false)
          setIsTakingLong(false)
          notifyUser('Stream could not start.')
        }
      } else {
        wantsPlaybackRef.current = false
        clearLoadTimers()
        clearRecoveryTimer()
        clearBufferRecoveryTimer()
        clearProgressWatch()
        audio.pause()
        setIsLoading(false)
        setIsTakingLong(false)
      }

      return
    }

    requestGenerationRef.current += 1
    const generation = requestGenerationRef.current

    wantsPlaybackRef.current = true
    reconnectAttemptsRef.current = 0
    clearRecoveryTimer()
    clearBufferRecoveryTimer()
    channelRef.current = nextChannel
    setChannel(nextChannel)
    setCurrentTrack(null)
    setErrorMessage(null)
    setIsLoading(true)
    setIsTakingLong(false)
    markMediaProgress()
    startProgressWatch()
    audio.pause()
    audio.src = resolveMediaUrl(nextChannel.stream_url)
    audio.volume = volumeRef.current
    audio.load()
    beginConnectionWatch(generation)
    void refreshNowPlaying(nextChannel, generation)

    try {
      await audio.play()
      setErrorMessage(null)
    } catch (error) {
      if (generation === requestGenerationRef.current) {
        console.warn('Unable to start live stream playback', { channel: nextChannel.slug, error })
        clearLoadTimers()
        clearProgressWatch()
        wantsPlaybackRef.current = false
        setErrorMessage('Live stream could not start. Use retry or choose another channel.')
        setIsPlaying(false)
        setIsLoading(false)
        setIsTakingLong(false)
        notifyUser('Stream could not start.')
      }
    }
  }, [
    beginConnectionWatch,
    clearBufferRecoveryTimer,
    clearLoadTimers,
    clearProgressWatch,
    clearRecoveryTimer,
    markMediaProgress,
    notifyUser,
    refreshNowPlaying,
    startProgressWatch
  ])

  const retry = useCallback(async (): Promise<void> => {
    const currentChannel = channelRef.current
    const audio = audioRef.current

    if (!currentChannel || !audio) {
      return
    }

    requestGenerationRef.current += 1
    const generation = requestGenerationRef.current

    wantsPlaybackRef.current = true
    reconnectAttemptsRef.current = 0
    clearRecoveryTimer()
    clearBufferRecoveryTimer()
    setCurrentTrack(null)
    setErrorMessage(null)
    setIsLoading(true)
    setIsPlaying(false)
    setIsTakingLong(false)
    markMediaProgress()
    startProgressWatch()
    audio.pause()
    audio.src = resolveMediaUrl(currentChannel.stream_url)
    audio.volume = volumeRef.current
    audio.load()
    beginConnectionWatch(generation)
    void refreshNowPlaying(currentChannel, generation)

    try {
      await audio.play()
    } catch (error) {
      if (generation === requestGenerationRef.current) {
        console.warn('Unable to retry live stream playback', { channel: currentChannel.slug, error })
        clearLoadTimers()
        clearProgressWatch()
        wantsPlaybackRef.current = false
        setErrorMessage('Live stream could not start. Use retry or choose another channel.')
        setIsLoading(false)
        setIsTakingLong(false)
        notifyUser('Stream could not start.')
      }
    }
  }, [
    beginConnectionWatch,
    clearBufferRecoveryTimer,
    clearLoadTimers,
    clearProgressWatch,
    clearRecoveryTimer,
    markMediaProgress,
    notifyUser,
    refreshNowPlaying,
    startProgressWatch
  ])

  const setVolume = useCallback((value: number): void => {
    const nextVolume = clampVolume(value)

    volumeRef.current = nextVolume
    setVolumeState(nextVolume)

    if (audioRef.current) {
      audioRef.current.volume = nextVolume
    }
  }, [])

  useEffect(() => {
    if (!channel) {
      return undefined
    }

    const interval = window.setInterval(() => {
      void refreshNowPlaying(channel)
    }, NOW_PLAYING_REFRESH_MS)

    return () => {
      window.clearInterval(interval)
    }
  }, [channel, refreshNowPlaying])

  useEffect(() => {
    if (!supportsMediaSession() || typeof MediaMetadata === 'undefined') {
      return
    }

    const mediaSession = navigator.mediaSession
    const artworkUrl = currentTrack ? getAudioImageUrl(currentTrack, 'preview') ?? getAudioImageUrl(currentTrack, 'thumb') : null

    mediaSession.metadata = currentTrack
      ? new MediaMetadata({
        album: channel?.name ?? 'Mavolyra Live',
        artist: currentTrack.artist ?? channel?.name ?? 'Mavolyra',
        artwork: artworkUrl ? [{ src: resolveMediaUrl(artworkUrl), sizes: '512x512' }] : [],
        title: currentTrack.title
      })
      : null

    mediaSession.playbackState = isPlaying ? 'playing' : channel ? 'paused' : 'none'
  }, [channel, currentTrack, isPlaying])

  useEffect(() => {
    if (!supportsMediaSession()) {
      return
    }

    const mediaSession = navigator.mediaSession

    mediaSession.setActionHandler('play', () => {
      if (channelRef.current) {
        void playChannel(channelRef.current)
      }
    })
    mediaSession.setActionHandler('pause', () => audioRef.current?.pause())
    mediaSession.setActionHandler('stop', stop)

    return () => {
      mediaSession.setActionHandler('play', null)
      mediaSession.setActionHandler('pause', null)
      mediaSession.setActionHandler('stop', null)
      mediaSession.metadata = null
      mediaSession.playbackState = 'none'
    }
  }, [playChannel, stop])

  return {
    channel,
    currentTrack,
    audioElement,
    errorMessage,
    isLoading,
    isPlaying,
    isTakingLong,
    playChannel,
    refreshNowPlaying,
    retry,
    setVolume,
    stop,
    volume
  }
}
